import React, { useContext, useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { toast } from "react-toastify";
import AppModal from "../AppModal";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import useWhatsApps from "../../hooks/useWhatsApps";

const UserSchema = Yup.object().shape({
	name: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
	password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email").required("Required"),
});

const initialState = {
	name: "",
	email: "",
	password: "",
	profile: "user",
	canSeeAllTickets: false,
	canSeeGroups: false,
	canDeleteMessages: false,
	canUsePersonalAi: false,
	personalAiMonthlyLimit: 0,
	usernameSIP: "",
	secretSIP: "",
};

function SectionCard({ title, description, children }) {
	return (
		<section className="rounded-2xl border border-default-200 bg-default-50 p-4 dark:border-default-700 dark:bg-default-900">
			<div className="mb-4">
				<div>
					<h3 className="m-0 text-sm font-semibold text-default-900 dark:text-default-100">
						{title}
					</h3>
					{description && (
						<p className="mb-0 mt-1 text-sm text-default-500 dark:text-default-400">
							{description}
						</p>
					)}
				</div>
			</div>
			<div className="space-y-4">{children}</div>
		</section>
	);
}

function IconChevronDown() {
	return (
		<svg className="size-4 shrink-0 text-default-500 dark:text-default-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="m6 9 6 6 6-6" />
		</svg>
	);
}

function IconEye({ open }) {
	return open ? (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.77 21.77 0 0 1 5.06-5.94" />
			<path d="M1 1l22 22" />
			<path d="M9.53 9.53A3 3 0 0 0 12 15a3 3 0 0 0 2.47-5.47" />
			<path d="M14.47 14.47A10.94 10.94 0 0 0 23 12s-4-8-11-8a10.94 10.94 0 0 0-4.53 1" />
		</svg>
	) : (
		<svg className="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12Z" />
			<circle cx="12" cy="12" r="3" />
		</svg>
	);
}

function CheckboxField({ label, checked, onChange }) {
	return (
		<label className="flex cursor-pointer items-start gap-3 rounded-xl border border-default-200 bg-white px-3 py-2.5 text-sm text-default-700 transition hover:border-default-300 dark:border-default-700 dark:bg-default-950 dark:text-default-200 dark:hover:border-default-600">
			<input
				type="checkbox"
				className="mt-0.5 size-4 rounded border-default-300 text-primary focus:ring-primary"
				checked={checked}
				onChange={onChange}
			/>
			<span className="leading-5">{label}</span>
		</label>
	);
}

function MultiSelectField({
	label,
	options,
	selectedIds,
	onChange,
	placeholder,
	emptyText,
}) {
	const [open, setOpen] = useState(false);
	const containerRef = useRef(null);

	useEffect(() => {
		if (!open) return undefined;

		const handleClickOutside = event => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [open]);

	const selectedOptions = options.filter(option =>
		selectedIds.some(id => String(id) === String(option.id))
	);

	const summary =
		selectedOptions.length === 0
			? placeholder
			: selectedOptions.length <= 2
				? selectedOptions.map(option => option.name).join(", ")
				: `${selectedOptions.length} filas selecionadas`;

	const handleToggle = optionId => {
		const exists = selectedIds.some(id => String(id) === String(optionId));
		onChange(
			exists
				? selectedIds.filter(id => String(id) !== String(optionId))
				: [...selectedIds, optionId]
		);
	};

	return (
		<div ref={containerRef} className="relative">
			<label className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100">
				{label}
			</label>
			<button
				type="button"
				className="form-input flex w-full items-center justify-between gap-3 text-left"
				onClick={() => setOpen(current => !current)}
			>
				<span
					className={
						selectedOptions.length > 0
							? "text-default-900 dark:text-default-100"
							: "text-default-500 dark:text-default-400"
					}
				>
					{summary}
				</span>
				<IconChevronDown />
			</button>

			{selectedOptions.length > 0 && (
				<div className="mt-2 flex flex-wrap gap-2">
					{selectedOptions.map(queue => (
						<button
							key={queue.id}
							type="button"
							className="inline-flex items-center gap-2 rounded-full border border-default-200 bg-default-100 px-3 py-1 text-xs font-medium text-default-700 transition hover:border-default-300 hover:bg-default-150 dark:border-default-700 dark:bg-default-800 dark:text-default-200 dark:hover:border-default-600"
							onClick={() => handleToggle(queue.id)}
						>
							<span
								className="size-2.5 rounded-full"
								style={{ backgroundColor: queue.color || "#3b82f6" }}
							/>
							<span>{queue.name}</span>
							<span className="text-[11px] text-default-500 dark:text-default-400">
								x
							</span>
						</button>
					))}
				</div>
			)}

			{open && (
				<div className="absolute left-0 right-0 z-30 mt-2 max-h-60 overflow-auto rounded-xl border border-default-200 bg-white p-2 shadow-lg dark:border-default-700 dark:bg-default-950">
					{options.length > 0 ? (
						options.map(queue => {
							const checked = selectedIds.some(id => String(id) === String(queue.id));

							return (
								<label
									key={queue.id}
									className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 hover:bg-default-100 dark:hover:bg-default-800"
								>
									<input
										type="checkbox"
										className="size-4 rounded border-default-300 text-primary focus:ring-primary"
										checked={checked}
										onChange={() => handleToggle(queue.id)}
									/>
									<span
										className="size-3 rounded-full"
										style={{ backgroundColor: queue.color || "#3b82f6" }}
									/>
									<span className="text-sm text-default-800 dark:text-default-100">
										{queue.name}
									</span>
								</label>
							);
						})
					) : (
						<p className="mb-0 px-3 py-2 text-sm text-default-500 dark:text-default-400">
							{emptyText}
						</p>
					)}
				</div>
			)}
		</div>
	);
}

function UserModal({ open, onClose, userId }) {
	const { user: loggedInUser } = useContext(AuthContext);
	const [user, setUser] = useState(initialState);
	const [queues, setQueues] = useState([]);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [showPassword, setShowPassword] = useState(false);
	const [whatsappId, setWhatsappId] = useState("");
	const [canSeeAllTickets, setCanSeeAllTickets] = useState(false);
	const [canSeeGroups, setCanSeeGroups] = useState(false);
	const [canDeleteMessages, setCanDeleteMessages] = useState(false);
	const [canUsePersonalAi, setCanUsePersonalAi] = useState(false);
	const [personalAiMonthlyLimit, setPersonalAiMonthlyLimit] = useState(0);
	const [personalAiTokensUsed, setPersonalAiTokensUsed] = useState(0);
	const [loadingUser, setLoadingUser] = useState(false);
	const { loading, whatsApps } = useWhatsApps();
	const [userSeeNumbers ,setUserSeeNumbers] = useState(true)

	useEffect(() => {
		if (!open) return undefined;

		let cancelled = false;

		(async () => {
			try {
				const { data } = await api.get("/queue");
				if (!cancelled) {
					setQueues(Array.isArray(data) ? data : []);
				}
			} catch (err) {
				if (!cancelled) toastError(err);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open]);

	useEffect(() => {
		if (!open) return undefined;

		if (!userId) {
			setUser(initialState);
			setSelectedQueueIds([]);
			setWhatsappId("");
			setCanSeeAllTickets(false);
			setCanSeeGroups(false);
			setCanDeleteMessages(false);
			setCanUsePersonalAi(false);
			setPersonalAiMonthlyLimit(0);
			setPersonalAiTokensUsed(0);
			setLoadingUser(false);
			return undefined;
		}

		const fetchUser = async () => {
			setLoadingUser(true);
			try {
				const { data } = await api.get(`/users/${userId}`);
				setUser(prevState => ({ ...prevState, ...data }));
				setSelectedQueueIds(data.queues?.map(queue => queue.id) || []);
				setWhatsappId(data.whatsappId || "");
				setCanSeeAllTickets(Boolean(data.canSeeAllTickets));
				setCanSeeGroups(Boolean(data.canSeeGroups));
				setCanDeleteMessages(Boolean(data.canDeleteMessages));
				setCanUsePersonalAi(Boolean(data.canUsePersonalAi));
				setPersonalAiMonthlyLimit(Number(data.personalAiMonthlyLimit) || 0);
				setPersonalAiTokensUsed(Number(data.personalAiTokensUsed) || 0);
				setUserSeeNumbers(Boolean(data.userSeeNumbers));
			} catch (err) {
				toastError(err);
			} finally {
				setLoadingUser(false);
			}
		};

		fetchUser();
	}, [open, userId]);

	const handleClose = () => {
		onClose();
		setUser(initialState);
		setSelectedQueueIds([]);
		setWhatsappId("");
		setCanSeeAllTickets(false);
		setCanSeeGroups(false);
		setCanDeleteMessages(false);
		setCanUsePersonalAi(false);
		setPersonalAiMonthlyLimit(0);
		setPersonalAiTokensUsed(0);
		setShowPassword(false);
	};

	const handleSaveUser = async values => {
		const userData = {
			...values,
			canSeeAllTickets,
			canSeeGroups,
			canDeleteMessages,
			canUsePersonalAi,
			personalAiMonthlyLimit: Number(personalAiMonthlyLimit) || 0,
			userSeeNumbers,
			whatsappId: whatsappId || null,
			queueIds: selectedQueueIds,
		};

		try {
			if (userId) {
				await api.put(`/users/${userId}`, userData);
			} else {
				await api.post("/users", userData);
			}
			toast.success(i18n.t("userModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<AppModal
			open={open}
			onClose={handleClose}
			title={
				userId
					? i18n.t("userModal.title.edit")
					: i18n.t("userModal.title.add")
			}
			subtitle="Permissões, filas e conexão do atendente"
			maxWidth={820}
			bodyClassName="user-modal__body"
			footer={null}
		>
			<Formik
				initialValues={user}
				enableReinitialize
				validationSchema={UserSchema}
				onSubmit={async (values, actions) => {
					await handleSaveUser(values);
					actions.setSubmitting(false);
				}}
			>
				{({ values, touched, errors, isSubmitting, handleChange, handleBlur }) => (
					<Form className="space-y-3">
						{loadingUser ? (
							<div className="app-modal-loading">
								<div className="app-modal-spinner" />
							</div>
						) : (
							<>
								<SectionCard
									title="Dados principais"
									description="Defina os dados de acesso básicos do usuário."
								>
									<div className="grid gap-4 lg:grid-cols-2">
										<div>
											<label
												htmlFor="user-name"
												className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
											>
												{i18n.t("userModal.form.name")}
											</label>
											<input
												id="user-name"
												name="name"
												type="text"
												autoFocus
												className="form-input w-full"
												value={values.name || ""}
												onChange={handleChange}
												onBlur={handleBlur}
											/>
											{touched.name && errors.name && (
												<p className="mt-1 text-sm text-danger">{errors.name}</p>
											)}
										</div>

										<div>
											<label
												htmlFor="user-email"
												className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
											>
												{i18n.t("userModal.form.email")}
											</label>
											<input
												id="user-email"
												name="email"
												type="email"
												className="form-input w-full"
												value={values.email || ""}
												onChange={handleChange}
												onBlur={handleBlur}
											/>
											{touched.email && errors.email && (
												<p className="mt-1 text-sm text-danger">{errors.email}</p>
											)}
										</div>

										<div>
											<label
												htmlFor="user-password"
												className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
											>
												{i18n.t("userModal.form.password")}
											</label>
											<div className="relative">
												<input
													id="user-password"
													name="password"
													type={showPassword ? "text" : "password"}
													className="form-input w-full pe-11"
													value={values.password || ""}
													onChange={handleChange}
													onBlur={handleBlur}
												/>
												<button
													type="button"
													className="absolute inset-y-0 end-0 flex items-center pe-3 text-default-500 transition hover:text-default-700 dark:text-default-400 dark:hover:text-default-200"
													onClick={() => setShowPassword(current => !current)}
													aria-label="Mostrar senha"
												>
													<IconEye open={showPassword} />
												</button>
											</div>
											{touched.password && errors.password && (
												<p className="mt-1 text-sm text-danger">{errors.password}</p>
											)}
										</div>

										<Can
											role={loggedInUser.profile}
											perform="user-modal:editProfile"
											yes={() => (
												<div>
													<label
														htmlFor="user-profile"
														className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
													>
														{i18n.t("userModal.form.profile")}
													</label>
													<select
														id="user-profile"
														name="profile"
														className="form-select w-full"
														value={values.profile || "user"}
														onChange={handleChange}
													>
														<option value="admin">
															{i18n.t("userModal.form.admin")}
														</option>
														<option value="user">
															{i18n.t("userModal.form.user")}
														</option>
													</select>
												</div>
											)}
										/>
									</div>
								</SectionCard>

								<SectionCard
									title="Integração SIP"
									description="Campos opcionais para integração de telefonia."
								>
									<div className="grid gap-4 lg:grid-cols-2">
										<div>
											<label
												htmlFor="user-username-sip"
												className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
											>
												usernameSIP
											</label>
											<input
												id="user-username-sip"
												name="usernameSIP"
												type="text"
												className="form-input w-full"
												value={values.usernameSIP || ""}
												onChange={handleChange}
												onBlur={handleBlur}
											/>
										</div>

										<div>
											<label
												htmlFor="user-secret-sip"
												className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
											>
												secretSIP
											</label>
											<input
												id="user-secret-sip"
												name="secretSIP"
												type="text"
												className="form-input w-full"
												value={values.secretSIP || ""}
												onChange={handleChange}
												onBlur={handleBlur}
											/>
										</div>
									</div>
								</SectionCard>

								<Can
									role={loggedInUser.profile}
									perform="user-modal:editQueues"
									yes={() => (
										<SectionCard
											title="Acesso operacional"
											description="Defina filas, conexão padrão e permissões adicionais."
										>
											<MultiSelectField
												label={i18n.t("queueSelect.inputLabel")}
												options={queues}
												selectedIds={selectedQueueIds}
												onChange={setSelectedQueueIds}
												placeholder="Selecione as filas"
												emptyText="Nenhuma fila disponível"
											/>

											{!loading && (
												<div className="grid gap-4 lg:grid-cols-2">
													<div>
														<label
															htmlFor="user-whatsapp"
															className="mb-2 block text-sm font-medium text-default-800 dark:text-default-100"
														>
															{i18n.t("userModal.form.whatsapp")}
														</label>
														<select
															id="user-whatsapp"
															className="form-select w-full"
															value={whatsappId}
															onChange={event => setWhatsappId(event.target.value)}
														>
															<option value="">Nenhuma conexão</option>
															{whatsApps.map(whatsapp => (
																<option key={whatsapp.id} value={whatsapp.id}>
																	{whatsapp.name}
																</option>
															))}
														</select>
													</div>
												</div>
											)}

											<div className="grid gap-3 lg:grid-cols-1">
												<CheckboxField
													label="Usuário pode ver todos os tickets"
													checked={canSeeAllTickets}
													onChange={() =>
														setCanSeeAllTickets(current => !current)
													}
												/>
												<CheckboxField
													label="Usuário pode ver números de contatos"
													checked={userSeeNumbers}
													onChange={() =>
														setUserSeeNumbers(current => !current)
													}
												/>
												<CheckboxField
													label="Usuário pode ver todos os grupos"
													checked={canSeeGroups}
													onChange={() => setCanSeeGroups(current => !current)}
												/>
												<CheckboxField
													label="Usuário pode deletar mensagens"
													checked={canDeleteMessages}
													onChange={() =>
														setCanDeleteMessages(current => !current)
													}
												/>
												{values.profile === "admin" ? (
													<p className="mb-0 text-sm text-default-500 dark:text-default-400">
														Administradores têm o Assistente geral liberado
														automaticamente, sem cota individual (consome tokens da empresa).
													</p>
												) : (
													<>
														<CheckboxField
															label="Pode usar Assistente geral (IA)"
															checked={canUsePersonalAi}
															onChange={() =>
																setCanUsePersonalAi(current => !current)
															}
														/>
														{canUsePersonalAi && (
															<div className="grid gap-2">
																<label className="text-sm text-default-700 dark:text-default-200">
																	Limite mensal de tokens (unidades)
																	<input
																		type="number"
																		min={1}
																		className="form-input mt-1 w-full"
																		value={personalAiMonthlyLimit}
																		onChange={e =>
																			setPersonalAiMonthlyLimit(
																				Math.max(0, Number(e.target.value) || 0)
																			)
																		}
																	/>
																</label>
																<p className="mb-0 text-xs text-default-500 dark:text-default-400">
																	Defina quanto essa pessoa pode gastar por mês.
																	{userId
																		? ` Uso atual: ${personalAiTokensUsed} / ${personalAiMonthlyLimit || "—"}`
																		: ""}
																</p>
															</div>
														)}
													</>
												)}
											</div>
										</SectionCard>
									)}
								/>
							</>
						)}

						<div className="flex justify-end gap-2 border-t border-default-200 pt-4 dark:border-default-700">
							<button
								type="button"
								className="btn btn-sub-gray"
								onClick={handleClose}
								disabled={isSubmitting}
							>
								{i18n.t("userModal.buttons.cancel")}
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={isSubmitting || loadingUser}
							>
								{isSubmitting
									? "Salvando..."
									: userId
										? i18n.t("userModal.buttons.okEdit")
										: i18n.t("userModal.buttons.okAdd")}
							</button>
						</div>
					</Form>
				)}
			</Formik>
		</AppModal>
	);
}

export default UserModal;
